/**
 * phaseConjugatePipeline — phase-conjugate light: light traveling
 * backward in time.
 *
 * Phase conjugation is a non-linear optical technique where a beam
 * of light is reflected such that the phase of the wavefront is
 * reversed. The result is a "time-reversed" beam that perfectly
 * converges back to its source, even through distorting media.
 *
 * Innovations:
 *   - We show two light beams: one propagating forward (from a
 *     "source" to a "scatterer"), and a phase-conjugate one
 *     propagating backward (re-converging to the source).
 *   - The forward beam distorts when it hits a "distorting medium"
 *     in the middle of the screen; the phase-conjugate beam
 *     re-converges through the medium.
 *   - Caustics: where the conjugate beam converges, bright
 *     caustic patterns appear.
 *   - The "scatterer" is a moving object (rotating shape) that
 *     distorts the beam.
 *   - Audio: bass shifts the scatterer position, mid drives
 *     the distortion intensity, high drives the caustic brightness.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface PhaseConjugatePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — dark with a faint holographic shimmer.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.02, 0.04, 0.10),
    vec3(0.005, 0.005, 0.02),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // === Source (left) ===
  vec2 sourcePos = vec2(-0.85, 0.0);
  float sourceD = length(p - sourcePos);
  col += vec3(0.85, 0.95, 1.0) * exp(-sourceD * 200.0) * 1.5;
  // Source halo.
  col += vec3(0.6, 0.85, 1.0) * exp(-sourceD * 8.0) * 0.3;

  // === Scatterer (center) ===
  // A rotating shape that distorts the beam.
  vec2 scatterPos = vec2(0.0, 0.0);
  float ang = uTime * 0.5;
  float ca = cos(ang), sa = sin(ang);
  vec2 pp = p - scatterPos;
  pp = vec2(ca * pp.x - sa * pp.y, sa * pp.x + ca * pp.y);
  float scatterShape = 0.0;
  // A 5-pointed star.
  float scatterR = 0.15 + 0.02 * mid;
  float scatterAng = atan(pp.y, pp.x);
  float scatterDist = length(pp);
  float starPattern = abs(sin(scatterAng * 2.5));
  float starEdge = abs(scatterDist - scatterR * (0.8 + 0.2 * starPattern));
  // Draw the star as an outline.
  float scatterMask = 1.0 - smoothstep(0.0, 0.005, starEdge);
  col += vec3(0.95, 0.7, 0.4) * scatterMask * 0.8;

  // === Target (right) ===
  vec2 targetPos = vec2(0.85, 0.0);
  float targetD = length(p - targetPos);
  // The target is a "broken" receiver — a cluster of dots.
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    vec2 dotPos = targetPos + vec2(
      cos(fi * 1.2) * 0.04,
      sin(fi * 1.2) * 0.04
    );
    float dotD = length(p - dotPos);
    col += vec3(0.7, 0.95, 0.7) * exp(-dotD * 600.0) * 0.7;
  }

  // === Forward beam (from source to target, distorted by scatterer) ===
  // The beam is a thin line from sourcePos to targetPos, distorted by
  // the scatterer.
  float forwardBeam = 0.0;
  for (int i = 0; i < 60; i++) {
    float t = float(i) / 60.0;
    // Sample points along the source-to-target line.
    vec2 beamPos = mix(sourcePos, targetPos, t);
    // Perturb by the scatterer (the closer to the scatterer, the more
    // perturbation).
    float scatterInfluence = 1.0 - 4.0 * (t - 0.5) * (t - 0.5);
    scatterInfluence = max(scatterInfluence, 0.0);
    vec2 perturbation = vec2(
      sin(beamPos.y * 20.0 + uTime) * 0.05,
      cos(beamPos.x * 20.0 + uTime * 1.3) * 0.05
    ) * scatterInfluence;
    beamPos += perturbation;
    float d = length(p - beamPos);
    forwardBeam += exp(-d * 250.0);
  }
  col += vec3(0.5, 0.85, 0.95) * forwardBeam * 0.5;

  // === Phase-conjugate beam (from target back to source, undistorted) ===
  // The phase-conjugate beam retraces the same path but in reverse,
  // and re-converges at the source.
  float conjBeam = 0.0;
  for (int i = 0; i < 60; i++) {
    float t = float(i) / 60.0;
    vec2 beamPos = mix(targetPos, sourcePos, t);
    float scatterInfluence = 1.0 - 4.0 * (t - 0.5) * (t - 0.5);
    scatterInfluence = max(scatterInfluence, 0.0);
    vec2 perturbation = vec2(
      sin(beamPos.y * 20.0 + uTime) * 0.05,
      cos(beamPos.x * 20.0 + uTime * 1.3) * 0.05
    ) * scatterInfluence;
    beamPos += perturbation;
    float d = length(p - beamPos);
    conjBeam += exp(-d * 250.0);
  }
  col += vec3(0.95, 0.85, 0.5) * conjBeam * 0.5;

  // === Caustics at the convergence point ===
  // Where the conjugate beam converges (back at the source), bright
  // caustic patterns appear.
  float causticD = length(p - sourcePos);
  float caustic = 0.0;
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    float causticR = 0.05 + 0.05 * sin(uTime * 0.5 + fi) + 0.05 * fi;
    float cd = abs(causticD - causticR);
    caustic += exp(-cd * 50.0) * (0.5 + 0.5 * sin(causticD * 30.0 + uTime * 2.0 + fi));
  }
  col += vec3(1.0, 0.95, 0.7) * caustic * 0.4 * (0.5 + 0.5 * high);

  // Beam-crossing interference at the scatterer.
  float crossD = length(p - scatterPos);
  float cross = exp(-crossD * 8.0) * (0.5 + 0.5 * sin(crossD * 25.0 + uTime * 3.0));
  col += vec3(1.0, 0.95, 0.7) * cross * 0.3;

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computePhaseConjugateBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computePhaseConjugateRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createPhaseConjugatePipeline(gl: WebGL2RenderingContext): PhaseConjugatePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawPhaseConjugate(
  gl: WebGL2RenderingContext,
  pipe: PhaseConjugatePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
